import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { API } from '../../config/api';
import { TrendingUp, DollarSign, ShoppingCart, Clock } from 'lucide-react';
import { ReceiptsPanel } from './ReceiptsPanel';

export function SalesDashboard({ openReceiptSession, onReceiptOpened }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      const { data: d } = await api.get(`${API}/analytics/sales`, { withCredentials: true });
      setData(d);
    } catch {
      // analytics fetch is non-critical — UI shows fallback state
    }
    setLoading(false);
  };

  if (loading) return <div className="text-center py-12 text-[#A3A3A3]">Loading sales data...</div>;
  if (!data) return <div className="text-center py-12 text-[#A3A3A3]">Failed to load sales data</div>;

  const typeColors = { track: '#FF3B30', merch: '#DAA520', beat: '#8B5CF6', donation: '#1DB954' };
  const typeLabels = { track: 'Tracks', merch: 'Merch', beat: 'Beats', donation: 'Donations' };

  const maxRevenue = Math.max(...(data.daily_revenue || []).map(d => d.revenue), 1);

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl" data-testid="total-revenue-card">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-[#DAA520]" />
            <span className="text-xs text-[#A3A3A3] uppercase">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold font-mono text-[#DAA520]">${data.total_revenue.toFixed(2)}</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl" data-testid="total-orders-card">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="w-4 h-4 text-[#FF3B30]" />
            <span className="text-xs text-[#A3A3A3] uppercase">Total Orders</span>
          </div>
          <p className="text-2xl font-bold font-mono">{data.total_orders}</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl" data-testid="pending-orders-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-[#FF6B35]" />
            <span className="text-xs text-[#A3A3A3] uppercase">Pending</span>
          </div>
          <p className="text-2xl font-bold font-mono text-[#FF6B35]">{data.pending_orders}</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-[#1DB954]" />
            <span className="text-xs text-[#A3A3A3] uppercase">Avg Order</span>
          </div>
          <p className="text-2xl font-bold font-mono">
            ${data.total_orders > 0 ? (data.total_revenue / data.total_orders).toFixed(2) : '0.00'}
          </p>
        </div>
      </div>

      {/* Revenue by Type */}
      <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#DAA520] mb-4">Revenue by Type</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(data.revenue_by_type).map(([type, info]) => (
            <div key={type} className="bg-[#0A0A0A] border border-[#262626] p-3 rounded-lg" data-testid={`revenue-type-${type}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: typeColors[type] || '#666' }} />
                <span className="text-xs font-bold uppercase">{typeLabels[type] || type}</span>
              </div>
              <p className="text-lg font-bold font-mono" style={{ color: typeColors[type] || '#fff' }}>${info.total.toFixed(2)}</p>
              <p className="text-[10px] text-[#666]">{info.count} order{info.count !== 1 ? 's' : ''}</p>
            </div>
          ))}
          {Object.keys(data.revenue_by_type).length === 0 && (
            <p className="text-sm text-[#A3A3A3] col-span-4">No paid orders yet</p>
          )}
        </div>
      </div>

      {/* Revenue Chart (last 30 days) */}
      {data.daily_revenue.length > 0 && (
        <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#DAA520] mb-4">Last 30 Days</h3>
          <div className="flex items-end gap-1 h-32" data-testid="revenue-chart">
            {data.daily_revenue.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center group relative" title={`${d.date}: $${d.revenue.toFixed(2)} (${d.orders} orders)`}>
                <div className="w-full bg-[#DAA520] rounded-t-sm transition-all hover:bg-[#FFD700]"
                  style={{ height: `${Math.max(4, (d.revenue / maxRevenue) * 100)}%` }} />
                <div className="absolute -top-8 bg-[#0A0A0A] border border-[#262626] px-2 py-1 rounded text-[9px] font-mono text-[#DAA520] hidden group-hover:block whitespace-nowrap z-10">
                  ${d.revenue.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-[9px] text-[#666]">{data.daily_revenue[0]?.date}</span>
            <span className="text-[9px] text-[#666]">{data.daily_revenue[data.daily_revenue.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Top Sellers */}
      {data.top_sellers.length > 0 && (
        <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#DAA520] mb-4">Top Sellers</h3>
          <div className="space-y-2">
            {data.top_sellers.map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] p-3 rounded-lg" data-testid={`top-seller-${i}`}>
                <span className="text-lg font-bold text-[#333] w-8 text-center font-mono">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{item.title}</p>
                  <p className="text-[10px] text-[#A3A3A3] uppercase">{typeLabels[item.type] || item.type}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-bold text-[#DAA520]">${item.revenue.toFixed(2)}</p>
                  <p className="text-[10px] text-[#666]">{item.sales} sale{item.sales !== 1 ? 's' : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="bg-[#141414] border border-[#262626] p-4 rounded-xl">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#DAA520] mb-4">Recent Transactions</h3>
        {data.recent_transactions.length > 0 ? (
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {data.recent_transactions.map((tx, i) => (
              <div key={i} className="flex items-center gap-3 p-2 border-b border-[#1a1a1a] last:border-0" data-testid={`transaction-${i}`}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: typeColors[tx.type] || '#666' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs truncate">{tx.metadata?.track_title || tx.metadata?.title || tx.type || 'Order'}</p>
                  <p className="text-[10px] text-[#666]">{tx.email} {tx.created_at ? `· ${new Date(tx.created_at).toLocaleDateString()}` : ''}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-mono font-bold">${(tx.amount || 0).toFixed(2)}</p>
                  <p className={`text-[9px] uppercase font-bold ${tx.payment_status === 'paid' ? 'text-[#1DB954]' : 'text-[#FF6B35]'}`}>
                    {tx.payment_status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#A3A3A3]">No transactions yet</p>
        )}
      </div>

      {/* All Receipts — searchable, filterable, click any row for the full receipt */}
      <ReceiptsPanel openReceiptSession={openReceiptSession} onReceiptOpened={onReceiptOpened} />
    </div>
  );
}
